/** Android's HTML, which is not the browser's HTML.
 *
 * `Text.Html` is rendered by
 *   HtmlCompat.fromHtml(html, FROM_HTML_MODE_COMPACT
 *                               .xor(SEPARATOR_LINE_BREAK_PARAGRAPH)
 *                               .xor(SEPARATOR_LINE_BREAK_HEADING))   // = 60
 * which produces a flat Spannable: inline spans, and **newline characters** between
 * blocks. It does not lay blocks out — there are no margins, no CSS, and class
 * attributes are ignored entirely. Rendering the same string as real HTML in a browser
 * is *more* capable and therefore *less* faithful.
 *
 * The flag semantics are the opposite of what they look like. AOSP's
 * `HtmlToSpannedConverter.getMargin`:
 *
 *     if ((flag & mFlags) != 0) return 1;   // bit SET   -> ONE newline
 *     return 2;                             // bit CLEAR -> TWO newlines (blank line)
 *
 * COMPACT (63) has all six separator bits set. XOR-ing off PARAGRAPH (1) and HEADING (2)
 * CLEARS them, so at flags 60 `<p>` and `<h1..h6>` get a **blank line**, while
 * `<ul>/<li>/<div>/<blockquote>` keep a single newline.
 *
 * `Text.Raw(removeHtml = true)` is a second, different rule: plain COMPACT (63), so
 * every block boundary is a single newline, and the result is stripped to text.
 *
 * Spec §5.3.
 */
import DOMPurify from 'dompurify';

/** Tags `Html.fromHtml` understands. Anything else contributes only its text. */
const ALLOWED_TAGS = [
  'br', 'p', 'div', 'span', 'blockquote', 'ul', 'ol', 'li',
  'b', 'strong', 'i', 'em', 'cite', 'dfn', 'u', 'del', 's', 'strike',
  'sup', 'sub', 'big', 'small', 'tt', 'font', 'a',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];

/** Android keeps only these. Notably NOT class or style — it has no CSS. */
const ALLOWED_ATTR = ['href', 'color', 'face'];

const BLOCK_MARGIN: Record<string, number> = {
  // cleared bits at flags 60 -> blank line
  p: 2, h1: 2, h2: 2, h3: 2, h4: 2, h5: 2, h6: 2,
  // set bits -> single newline
  div: 1, blockquote: 1, ul: 1, ol: 1, li: 1,
};

const INLINE_TAG: Record<string, string> = {
  b: 'b', strong: 'b',
  i: 'i', em: 'i', cite: 'i', dfn: 'i',
  u: 'u',
  del: 's', s: 's', strike: 's',
  sup: 'sup', sub: 'sub',
  big: 'big', small: 'small',
  tt: 'code',
  a: 'a', font: 'span', span: 'span',
};

export interface AndroidHtmlOptions {
  /** `Text.Html.linksClickable` — true for every use on the four screens. */
  linksClickable?: boolean;
}

/** One output chunk: literal text, a line break, or an inline-marked run. */
type Node = { text: string } | { br: number } | { tag: string; href?: string; children: Node[] };

/** A break, or text that is only whitespace — neither survives a block boundary. */
const isBlank = (n: Node): boolean => 'br' in n || ('text' in n && !n.text.trim());

function parse(html: string): Node[] {
  const doc = new DOMParser().parseFromString(
    DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR }),
    'text/html',
  );

  const out: Node[] = [];

  /** AOSP appends only the difference, so `</p><p>` yields one blank line, not two. */
  const ensureBreaks = (sink: Node[], want: number) => {
    // drop inter-block whitespace before counting: Android's parser ignores it
    while (sink.length && isBlank(sink[sink.length - 1]!)) sink.pop();
    if (sink.length === 0) return; // no leading newlines
    let have = 0;
    for (let i = sink.length - 1; i >= 0; i -= 1) {
      const n = sink[i]!;
      if ('br' in n) have += n.br;
      else break;
    }
    if (have < want) sink.push({ br: want - have });
  };

  const walk = (el: ChildNode, sink: Node[]) => {
    if (el.nodeType === 3) {
      // Android collapses runs of whitespace the way an HTML parser does.
      const text = (el.textContent ?? '').replace(/\s+/g, ' ');
      if (!text) return;
      // whitespace between blocks is layout, not content
      if (!text.trim() && (sink.length === 0 || 'br' in sink[sink.length - 1]!)) return;
      sink.push({ text });
      return;
    }
    if (el.nodeType !== 1) return;

    const tag = (el as Element).tagName.toLowerCase();

    if (tag === 'br') {
      sink.push({ br: 1 });
      return;
    }

    const margin = BLOCK_MARGIN[tag];
    if (margin) {
      ensureBreaks(sink, margin);
      el.childNodes.forEach((c) => walk(c, sink));
      ensureBreaks(sink, margin);
      return;
    }

    const inline = INLINE_TAG[tag];
    if (inline) {
      const children: Node[] = [];
      el.childNodes.forEach((c) => walk(c, children));
      const href = tag === 'a' ? (el as HTMLAnchorElement).getAttribute('href') ?? undefined : undefined;
      sink.push({ tag: inline, href, children });
      return;
    }

    // unknown tag: contributes its text only
    el.childNodes.forEach((c) => walk(c, sink));
  };

  doc.body.childNodes.forEach((c) => walk(c, out));

  // a Spannable carries no leading or trailing blank lines
  while (out.length && isBlank(out[0]!)) out.shift();
  while (out.length && isBlank(out[out.length - 1]!)) out.pop();
  return out;
}

/** Render to HTML that a `white-space: pre-wrap` container displays as Android does. */
function toHtml(nodes: Node[], opts: AndroidHtmlOptions): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  return nodes
    .map((n) => {
      if ('text' in n) return esc(n.text);
      if ('br' in n) return '\n'.repeat(n.br);
      const inner = toHtml(n.children, opts);
      if (n.tag === 'a') {
        if (!opts.linksClickable || !n.href) return inner;
        return `<a href="${esc(n.href)}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
      }
      return `<${n.tag}>${inner}</${n.tag}>`;
    })
    .join('');
}

/**
 * `Text.Html` — sanitised, block boundaries turned into newlines with Android's
 * margins, links optionally live. Render inside `white-space: pre-wrap`.
 */
export function androidHtml(html: string, opts: AndroidHtmlOptions = {}): string {
  const { linksClickable = true } = opts;
  return toHtml(parse(html), { linksClickable });
}

/**
 * `Text.Raw(value, removeHtml = true)` — the card-description rule. Plain COMPACT (63),
 * so every block boundary is a SINGLE newline, and the result is plain text.
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(
    DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR }),
    'text/html',
  );
  const blocks = new Set(Object.keys(BLOCK_MARGIN));
  let out = '';
  const walk = (el: ChildNode) => {
    if (el.nodeType === 3) {
      out += (el.textContent ?? '').replace(/\s+/g, ' ');
      return;
    }
    if (el.nodeType !== 1) return;
    const tag = (el as Element).tagName.toLowerCase();
    if (tag === 'br') {
      out += '\n';
      return;
    }
    const isBlock = blocks.has(tag);
    if (isBlock && out && !out.endsWith('\n')) out += '\n';
    el.childNodes.forEach(walk);
    if (isBlock && out && !out.endsWith('\n')) out += '\n';
  };
  doc.body.childNodes.forEach(walk);
  return out.trim();
}
